import {
  InteractionRequiredAuthError,
  ClientAuthError,
} from "@azure/msal-browser";
import { getCreActiveAccount, isCreAccount } from "./creAccountUtils";

export const TENANT_ID = "8b95a7b3-effb-4d96-a5c2-118fe047e71a";
export const CRE_CLIENT_ID = "c279b90c-a9e9-4934-9721-9fcd08c465a4";
export const API_CLIENT_ID = "790433ef-9282-4ca5-91df-bbb9369bb43e";

/** Must match Entra app registration (SPA redirect URI = app origin, e.g. https://localhost:3000). */
export function getCreRedirectUri() {
  if (typeof window === "undefined") return undefined;
  return window.location.origin;
}

export const creMsalConfig = {
  auth: {
    clientId: CRE_CLIENT_ID,
    authority: `https://creenterprise.ciamlogin.com/${TENANT_ID}`,
    knownAuthorities: ["creenterprise.ciamlogin.com"],
    redirectUri:
      typeof window !== "undefined" ? getCreRedirectUri() : undefined,
    postLogoutRedirectUri:
      typeof window !== "undefined" ? getCreRedirectUri() : undefined,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

export const creLoginRequest = {
  scopes: [`api://${API_CLIENT_ID}/access_as_user`],
  prompt: "select_account",
};

export const creApiRequest = {
  scopes: [`api://${API_CLIENT_ID}/access_as_user`],
};

export function isAuthorityMismatchError(err) {
  const code = err?.errorCode || "";
  const msg = String(err?.message || "");
  return (
    code === "authority_mismatch" ||
    msg.includes("authority_mismatch") ||
    (err instanceof ClientAuthError && code === "authority_mismatch")
  );
}

export async function getCreAccessToken(msalInstance, account) {
  const result = await acquireCreAccessTokenSilent(msalInstance, account);
  if (result.ok) return result.accessToken;

  if (
    result.code === "interaction_required" ||
    result.code === "authority_mismatch"
  ) {
    const creAccount =
      account && isCreAccount(account)
        ? account
        : getCreActiveAccount(msalInstance);

    if (result.code === "authority_mismatch") {
      await msalInstance.clearCache();
      msalInstance.setActiveAccount(null);
      msalInstance.loginRedirect(creLoginRequest);
      return null;
    }

    if (creAccount) {
      msalInstance.acquireTokenRedirect({
        ...creApiRequest,
        account: creAccount,
      });
    }
    return null;
  }

  if (result.code === "no_account") return null;
  throw new Error(result.message || result.code);
}

/**
 * Silent token acquire for MedMic refresh/connect — never redirects.
 *
 * @returns {Promise<
 *   | { ok: true, accessToken: string }
 *   | { ok: false, code: string, message: string, msalErrorCode?: string }
 * >}
 */
export async function acquireCreAccessTokenSilent(msalInstance, account) {
  const creAccount =
    account && isCreAccount(account)
      ? account
      : getCreActiveAccount(msalInstance);

  if (!creAccount) {
    return {
      ok: false,
      code: "no_account",
      message: "No CRE MSAL account.",
    };
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...creApiRequest,
      account: creAccount,
    });
    if (!response?.accessToken) {
      return {
        ok: false,
        code: "no_token",
        message: "MSAL silent acquire returned no token.",
      };
    }
    return { ok: true, accessToken: response.accessToken };
  } catch (err) {
    const msalErrorCode = err?.errorCode || err?.name || "";
    const msg = err instanceof Error ? err.message : String(err);

    if (isAuthorityMismatchError(err)) {
      return {
        ok: false,
        code: "authority_mismatch",
        message: msg,
        msalErrorCode,
      };
    }

    if (
      err instanceof InteractionRequiredAuthError ||
      msalErrorCode === "interaction_required" ||
      msalErrorCode === "login_required" ||
      msalErrorCode === "consent_required" ||
      /interaction_required|login_required|consent_required/i.test(msg)
    ) {
      return {
        ok: false,
        code: "interaction_required",
        message: msg,
        msalErrorCode,
      };
    }

    return {
      ok: false,
      code: "transient_msal_error",
      message: msg,
      msalErrorCode,
    };
  }
}
